import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { TriangleAlert, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';

interface DangerZoneCardProps {
  clientName: string | null;
  className?: string;
}

const CONFIRM_WORD = 'DELETE';

export function DangerZoneCard({ clientName, className }: DangerZoneCardProps) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const warningId = 'danger-zone-warning';
  const instructionId = 'danger-zone-instruction';

  const canConfirm = confirmText === CONFIRM_WORD;

  const handleOpenChange = (next: boolean) => {
    if (deleting) return; // prevent close during deletion
    if (!next) {
      setConfirmText('');
    }
    setOpen(next);
    if (next) {
      // focus input after animation frame
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleDelete = async () => {
    if (!canConfirm) return;
    setDeleting(true);
    try {
      await api.post('/api/v1/settings/danger-zone/delete-all', {});
      toast.success('All data deleted.');
      setOpen(false);
      await supabase.auth.signOut();
      window.location.href = '/auth';
    } catch {
      toast.error('Failed to delete data. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <>
      <Card className={`border-destructive ${className ?? ''}`} aria-labelledby="danger-zone-title">
        <CardHeader>
          <CardTitle id="danger-zone-title" className="flex items-center gap-2 text-destructive">
            <TriangleAlert className="h-5 w-5" aria-hidden="true" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-medium">Delete all data for this organization</p>
            <p
              id={warningId}
              className="mt-1 text-sm text-muted-foreground"
              aria-live="polite"
            >
              This will permanently delete all jobs, reports, rules, knowledge base documents,
              and audit logs for your organization. This action cannot be undone.
            </p>
          </div>
          <Button
            variant="destructive"
            aria-describedby={warningId}
            onClick={() => { handleOpenChange(true); }}
          >
            Delete Everything
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <AlertDialogContent
          role="alertdialog"
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-body"
        >
          <AlertDialogHeader>
            <AlertDialogTitle id="delete-dialog-title">Delete All Data</AlertDialogTitle>
            <AlertDialogDescription id="delete-dialog-body">
              This will permanently delete ALL jobs, reports, rules, knowledge base documents,
              and audit logs for <strong>{clientName ?? 'your organization'}</strong>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 py-2">
            <p id={instructionId} className="text-sm">
              Type <strong>DELETE</strong> to confirm:
            </p>
            <Label htmlFor="delete-confirm-input" className="sr-only">
              Type DELETE to confirm
            </Label>
            <Input
              id="delete-confirm-input"
              ref={inputRef}
              value={confirmText}
              onChange={(e) => { setConfirmText(e.target.value); }}
              placeholder="DELETE"
              aria-label="Type DELETE to confirm"
              aria-describedby={instructionId}
              disabled={deleting}
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <button
              className="inline-flex items-center justify-center gap-2 rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground shadow-sm transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              disabled={!canConfirm || deleting}
              onClick={() => { void handleDelete(); }}
              aria-disabled={!canConfirm || deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {deleting ? 'Deleting…' : 'Delete Everything'}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
